import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { MergedEducationalArtifactConfig } from '../educational-workflow-config.types';
import { WORKFLOW_SLOT_GENERATION_PATH } from '../models/conversation-state.model';
import type { SlotOptionJson } from './assistant-slot-filling.service';
import { fuzzyPickOption } from '../utils/fuzzy-match.util';
import { normalizeSlotText } from '../utils/slot-normalizer.util';
import { dedupeById } from '../utils/workflow-options.util';

const GENERATION_PATH_QUICK_ALIASES = [
  'quick',
  'generic',
  'simple',
  'fast',
  'defaults',
] as const;

const GENERATION_PATH_CUSTOM_ALIASES = [
  'custom',
  'full',
  'detailed',
  'form',
  'manual',
] as const;

export interface SlotMatchCandidate {
  label: string;
  value: string;
  aliases?: string[];
}

type DomainRow = { id: number; name: string };
type QuestionRow = { id: number; content: string };

/** Chip-style short label (mirrors buildChipsForSlot). */
export function chipShortLabel(text: string, maxLen = 48): string {
  const t = String(text ?? '').trim();
  if (!t) return '';
  return t.length > maxLen ? t.slice(0, maxLen - 1) + '…' : t;
}

@Injectable({
  providedIn: 'root',
})
export class AssistantWorkflowSlotResolverService {
  private readonly translate = inject(TranslateService);

  buildCandidatesForSlot(
    slotKey: string,
    merged: MergedEducationalArtifactConfig | null,
    domains: readonly DomainRow[],
    questions: readonly QuestionRow[]
  ): SlotMatchCandidate[] {
    if (slotKey === WORKFLOW_SLOT_GENERATION_PATH) {
      return [
        {
          label: this.translate.instant('voiceAssistant.workflow.pathGeneric'),
          value: 'quick',
          aliases: ['quick', 'generic', 'simple', 'fast', 'defaults'],
        },
        {
          label: this.translate.instant('voiceAssistant.workflow.pathCustom'),
          value: 'custom',
          aliases: ['custom', 'full', 'detailed', 'form', 'manual'],
        },
      ];
    }

    if (slotKey === 'topicDomain') {
      return dedupeById(domains)
        .slice(0, 12)
        .map(d => {
        const name = String(d.name ?? '');
        return {
          label: name || `Domain ${d.id}`,
          value: `id:${d.id}`,
          aliases: [name],
        };
      });
    }

    if (slotKey === 'learningFocus') {
      return dedupeById(questions)
        .slice(0, 12)
        .map(q => {
        const content = String(q.content ?? '');
        const short = chipShortLabel(content) || `Question ${q.id}`;
        const aliases = [content, short].filter((a, i, arr) => a && arr.indexOf(a) === i);
        return {
          label: short,
          value: `id:${q.id}`,
          aliases,
        };
      });
    }

    const slotMap = (merged?.shared?.slots ?? {}) as
      | Record<string, { options?: SlotOptionJson[] }>
      | undefined;
    const opts =
      slotMap?.[slotKey]?.options?.filter(
        o => o.value !== '' && !(o as { disabled?: boolean }).disabled
      ) ?? [];
    return opts.slice(0, 12).map(o => ({
      label: o.label,
      value: o.value,
      aliases: o.aliases,
    }));
  }

  resolveWorkflowSlotInput(
    slotKey: string,
    text: string,
    merged: MergedEducationalArtifactConfig | null,
    domains: readonly DomainRow[],
    questions: readonly QuestionRow[]
  ): string | null {
    if (slotKey === WORKFLOW_SLOT_GENERATION_PATH) {
      return this.resolveGenerationPathInput(text);
    }
    const candidates = this.buildCandidatesForSlot(slotKey, merged, domains, questions);
    if (!candidates.length) return null;
    return fuzzyPickOption(text, candidates);
  }

  /**
   * Quick vs Custom must be an explicit choice — fuzzy token matching wrongly maps
   * phrases like "want to create a song" to Custom via the substring "to" in "custom".
   */
  resolveGenerationPathInput(text: string): 'quick' | 'custom' | null {
    const normalized = normalizeSlotText(text);
    if (!normalized) return null;

    const words = normalized.split(/\s+/).filter(w => w.length >= 2);
    const wordSet = new Set(words);

    const matchesQuick =
      GENERATION_PATH_QUICK_ALIASES.some(
        alias => normalized === alias || wordSet.has(alias)
      );
    const matchesCustom =
      GENERATION_PATH_CUSTOM_ALIASES.some(
        alias => normalized === alias || wordSet.has(alias)
      );

    if (matchesQuick && !matchesCustom) return 'quick';
    if (matchesCustom && !matchesQuick) return 'custom';
    return null;
  }
}
